import { isRecord, loadJson, saveJson } from '../core/storage';
import type { DifficultyId, GameResult } from '../game/types';

export interface Settings {
  /** Master mute (the ♪ button / M key) — independent of the per-channel toggles. */
  muted: boolean;
  sfx: boolean;
  music: boolean;
  voice: boolean;
  reducedMotion: boolean;
  keyHints: boolean;
  difficulty: DifficultyId;
}

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

const finePointer = (): boolean => {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return true;
  }
};

export function defaultSettings(): Settings {
  return {
    muted: false,
    sfx: true,
    music: true,
    voice: true,
    reducedMotion: prefersReducedMotion(),
    keyHints: finePointer(),
    difficulty: 'standard',
  };
}

export function loadSettings(): Settings {
  const base = defaultSettings();
  const stored = loadJson<unknown>('settings', null);
  if (!isRecord(stored)) return base;
  const bool = (k: keyof Settings): boolean =>
    typeof stored[k] === 'boolean' ? (stored[k] as boolean) : (base[k] as boolean);
  const diff = stored.difficulty;
  return {
    muted: bool('muted'),
    sfx: bool('sfx'),
    music: bool('music'),
    voice: bool('voice'),
    reducedMotion: bool('reducedMotion'),
    keyHints: bool('keyHints'),
    difficulty:
      diff === 'trainee' || diff === 'standard' || diff === 'boss' ? diff : base.difficulty,
  };
}

export function saveSettings(s: Settings): void {
  saveJson('settings', s);
}

export interface Progress {
  shiftsWorked: number;
  shiftsSurvived: number;
  bestTips: number;
  lifetimeTips: number;
  lifetimeChairs: number;
  achievements: string[];
  /** Best result per daily shift number. */
  daily: Record<string, { tips: number; closed: boolean; share: string }>;
  seenIntro: boolean;
}

export function emptyProgress(): Progress {
  return {
    shiftsWorked: 0,
    shiftsSurvived: 0,
    bestTips: 0,
    lifetimeTips: 0,
    lifetimeChairs: 0,
    achievements: [],
    daily: {},
    seenIntro: false,
  };
}

export function loadProgress(): Progress {
  const base = emptyProgress();
  const stored = loadJson<unknown>('progress', null);
  if (!isRecord(stored)) return base;
  const num = (k: keyof Progress): number =>
    typeof stored[k] === 'number' ? (stored[k] as number) : 0;
  const achievements = Array.isArray(stored.achievements)
    ? stored.achievements.filter((a): a is string => typeof a === 'string')
    : [];
  const daily: Progress['daily'] = {};
  if (isRecord(stored.daily)) {
    for (const [k, v] of Object.entries(stored.daily)) {
      if (isRecord(v) && typeof v.tips === 'number') {
        daily[k] = {
          tips: v.tips,
          closed: v.closed === true,
          share: typeof v.share === 'string' ? v.share : '',
        };
      }
    }
  }
  return {
    shiftsWorked: num('shiftsWorked'),
    shiftsSurvived: num('shiftsSurvived'),
    bestTips: num('bestTips'),
    lifetimeTips: num('lifetimeTips'),
    lifetimeChairs: num('lifetimeChairs'),
    achievements,
    daily,
    seenIntro: stored.seenIntro === true,
  };
}

export function saveProgress(p: Progress): void {
  saveJson('progress', p);
}

/** Fold a finished shift into lifetime progress. Returns the updated copy. */
export function recordResult(
  p: Progress,
  r: GameResult,
  share: string,
  unlocked: readonly string[],
): Progress {
  const next: Progress = {
    ...p,
    shiftsWorked: p.shiftsWorked + 1,
    shiftsSurvived: p.shiftsSurvived + (r.reason === 'shiftComplete' ? 1 : 0),
    bestTips: Math.max(p.bestTips, r.tips),
    lifetimeTips: Math.round((p.lifetimeTips + r.tips) * 100) / 100,
    lifetimeChairs: p.lifetimeChairs + r.stats.chairsCaught,
    achievements: [...new Set([...p.achievements, ...unlocked])],
    daily: { ...p.daily },
  };
  if (r.mode === 'daily') {
    const key = String(r.shiftNumber);
    const prev = next.daily[key];
    if (!prev || r.tips > prev.tips)
      next.daily[key] = { tips: r.tips, closed: r.reason === 'closed', share };
  }
  return next;
}
